import { cacheAndPushRequestContext } from '~/utils/requestContext/pushRequestContext';
import { EMPTY_REQUEST_CONTEXT_PAYLOAD } from '~/utils/requestContext/types';

const DISABLED_GTM_ID = 'GTM-XXXXXXX';

function useConfiguredGtmScript() {
  const { gtm } = useRuntimeConfig().public;
  const id = gtm?.id?.trim();

  if (!id || id === DISABLED_GTM_ID) {
    return;
  }

  return useScriptGoogleTagManager({
    id,
    auth: gtm.auth || undefined,
    preview: gtm.env || undefined,
    cookiesWin: true,
    scriptOptions: {
      trigger: 'manual',
      // The container ID is supplied by runtimeConfig after the image build,
      // so Nuxt Scripts must not try to download and bundle gtm.js at build time.
      bundle: false
    }
  });
}

async function resolveRequestContextPayload(): Promise<unknown> {
  try {
    if (window.__requestContextPromise) {
      return await window.__requestContextPromise;
    }
  } catch {
    // Fall through to a direct fetch.
  }

  try {
    const response = await fetch('/api/request-context', {
      cache: 'no-store',
      credentials: 'same-origin'
    });

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch {
    return null;
  }
}

export default defineNuxtPlugin({
  name: 'request-context',
  enforce: 'pre',
  setup(nuxtApp) {
    void (async () => {
      const payload = await resolveRequestContextPayload();
      cacheAndPushRequestContext(payload ?? EMPTY_REQUEST_CONTEXT_PAYLOAD);
      // Nuxt Scripts queues gtm.js when the composable is created, even with
      // a manual trigger. Create it only after request_context is queued,
      // restoring Nuxt context after the asynchronous fetch.
      void nuxtApp.runWithContext(() => useConfiguredGtmScript()?.load());
    })();
  }
});
